import {useCallback, useState} from 'react';
import * as Sentry from '@sentry/react';
import {Navigate, useNavigate, useParams} from 'react-router-dom';
import {LivenessAction, IV_DOCUMENT_IMAGE_TYPES, IV_PHOTO_SOURCE} from '@chekin/core';
import {PATHS} from 'routes/constants';
import {useSummary} from 'context/summary';
import {useDocumentSelfieDetection, useIVComputedDetails} from 'hooks';
import {blurSelfiePhoto, LivenessDetection, useIVStore} from '@chekin/shared';
import ChekinProSettings from 'ChekinProSettings';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useHousing} from 'pages/SearchReservation/useHousing';

function SelfieLivenessView() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {reservationId, countryCode, data: summary} = useSummary();
  const {housing} = useHousing();
  const frontPhoto = useIVStore(state => state.imagesDetails.front);
  const livenessResultData = useIVStore(state => state.livenessResultData);
  const setLivenessResultData = useIVStore(state => state.setLivenessResultData);
  const docType = IV_DOCUMENT_IMAGE_TYPES.selfie;
  const {required} = useIVComputedDetails();
  const layout = useLayoutMode();

  const [livenessVideo, setLivenessVideo] = useState<Blob | null>(null);

  const {
    ProcessModal,
    ErrorModal,
    RetryModal,
    isProcessing,
    processState,
    sendSelfieToDetectFace,
  } = useDocumentSelfieDetection({docType, livenessVideo});

  const handleChangePhoto = useCallback(
    async ({
      screenshot,
      performedBy,
      completedActions,
      recordingVideo,
    }: {
      screenshot: string | null;
      recordingVideo: Blob | null;
      performedBy: 'CLIENT' | 'SERVER';
      completedActions: LivenessAction[];
    }) => {
      if (!screenshot) return;

      if (countryCode) {
        try {
          const blurredPhoto = await blurSelfiePhoto({
            photo: screenshot,
          });

          if (blurredPhoto) {
            if (recordingVideo) {
              setLivenessVideo(recordingVideo);
            }
            sendSelfieToDetectFace({
              source: IV_PHOTO_SOURCE.appCamera,
              countryCode,
              reservationId,
              image: blurredPhoto,
            });
            setLivenessResultData({
              ...livenessResultData,
              performedBy,
              passed: true,
              completedActions,
            });
          }
        } catch (error) {
          throw new Error(error as string);
        }
      } else {
        console.error('countryCode is missing', summary);
        Sentry.captureMessage('countryCode is missing', {extra: {summary}});
      }
    },
    [
      countryCode,
      reservationId,
      sendSelfieToDetectFace,
      summary,
      setLivenessResultData,
      livenessResultData,
    ],
  );

  if (!frontPhoto.uploaded) {
    return <Navigate to={`../../${PATHS.verification.main}/${id ?? ''}`} />;
  }

  const isCameraVisible = Boolean(
    !isProcessing && processState !== 'error' && processState !== 'success',
  );

  return (
    <>
      {isCameraVisible && (
        <LivenessDetection
          forceLivenessMechanism={
            ChekinProSettings.isSDK ? 'SERVER' : housing?.force_liveness_mechanism
          }
          onComplete={handleChangePhoto}
          isRequired={required}
          onVerifyLater={() => {
            navigate(`../../${PATHS.addPersonalDataForm}`);
          }}
          layout={layout}
        />
      )}
      <ProcessModal />
      <RetryModal />
      <ErrorModal />
    </>
  );
}

export {SelfieLivenessView};
